import {createContext, useContext} from 'react'

import {logger} from '../../../utils/logging/logger'

import type {CapabilityContext} from '../../../brand/capabilities/types/core'

/**
 * Safe defaults applied when no `<CapabilityRuntimeContext.Provider>` is
 * mounted above a consumer. Mirrors the implicit defaults the previous
 * `useCapabilityContext()` implementation in `shared` produced when
 * called outside `<DeviceLayoutProvider>` (atlas's `DeviceLayoutContext`
 * defaults to `desktopMd`, i.e. `isMobile: false`). Preserving that
 * behavior keeps top-of-shell consumers (e.g. checkout's `AppProviders`)
 * working without restructure while still letting deeper consumers read
 * the real, atlas-resolved context.
 */
const DEFAULT_CAPABILITY_CONTEXT: CapabilityContext = {
  isMobile: false,
  locale: 'en',
  isAuthenticated: false,
  experimentVariant: () => undefined
}

/**
 * React context that carries the runtime inputs capability resolvers need
 * (`isMobile`, `locale`, `isAuthenticated`, `experimentVariant`).
 *
 * The value is produced once, near the top of each app shell, by
 * `useCapabilityContext` in `@vio/shared/src/whiteLabel/hooks/`. That hook
 * reads from atlas (`useDeviceLayout`) and `core` redux state — neither of
 * which can be assembled inside `packages/core` without creating a
 * `core → atlas` circular package dependency.
 *
 * Mounting `<CapabilityRuntimeProvider>` (from
 * `@vio/shared/src/whiteLabel/CapabilityRuntimeProvider`) at the shell
 * lets `useBrandCapabilities` (which lives in `core`) read the resolved
 * context without touching atlas.
 */
export const CapabilityRuntimeContext = createContext<CapabilityContext>(
  DEFAULT_CAPABILITY_CONTEXT
)

/**
 * Read the runtime capability context. Falls back to safe defaults when
 * called outside a `<CapabilityRuntimeContext.Provider>` — matches the
 * pre-sc-184670 behavior of `useCapabilityContext()` being called outside
 * `<DeviceLayoutProvider>`. Emits a one-shot dev-mode warning so missing
 * providers don't go unnoticed during refactors.
 */
export const useCapabilityRuntimeContext = (): CapabilityContext => {
  const ctx = useContext(CapabilityRuntimeContext)
  if (
    ctx === DEFAULT_CAPABILITY_CONTEXT &&
    process.env.NODE_ENV !== 'production'
  ) {
    warnOnce()
  }
  return ctx
}

let warned = false
const warnOnce = () => {
  if (warned) return
  warned = true
  logger.warn(
    'useBrandCapabilities() resolved with default runtime context — no ' +
      '<CapabilityRuntimeProvider> mounted above the caller. Real ' +
      'isMobile/locale/isAuthenticated values will not be reflected. Mount ' +
      '<CapabilityRuntimeProvider> from @vio/shared/src/whiteLabel/CapabilityRuntimeProvider ' +
      'in the app shell (inside <DeviceLayoutProvider>).'
  )
}
